//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useContext, useEffect, useRef } from 'react';

import {
  type AgentPage, type PagePublication, type UiAction
} from './contract';
import {
  type ActionHandler, PageSnapshotContext, type PageSnapshotContextValue
} from './PageSnapshotContext';

export const handlerKey = (page: AgentPage, kind: UiAction['kind']): string =>
  `${page}:${kind}`;

export function useAgentBridge(): PageSnapshotContextValue {
  const context = useContext(PageSnapshotContext);
  if (!context) {
    throw new Error(
      'useAgentBridge must be used within a PageSnapshotProvider'
    );
  }
  return context;
}

// A page publishes its snapshot for as long as it is mounted.  Pass a
// memoized value: every new reference republishes.
export function usePublishSnapshot(snapshot: PagePublication | null): void {
  const { publish } = useAgentBridge();
  useEffect(() => {
    if (snapshot === null) return;
    return publish(snapshot);
  }, [publish, snapshot]);
}

// A page registers what it can do for as long as it is mounted.  The
// registration is keyed on the page and kind alone: the handler is read
// through a ref at call time, so a page may pass whatever closure it has
// this render without re-registering, and the latest one is what runs.
export function useRegisterActionHandler(
  page: AgentPage, kind: UiAction['kind'], handler: ActionHandler
): void {
  const { registerHandler } = useAgentBridge();
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => registerHandler(handlerKey(page, kind),
                                  (action) => latest.current(action)),
            [registerHandler, page, kind]);
}
